import {
    Component,
    Element,
    Event,
    EventEmitter,
    Fragment,
    h,
    Host,
    Prop,
    State,
} from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import {
    ISearchFilter,
    SearchFilterSelectionType,
    SearchTagTypeEnum,
} from "./interface";
import {
    findTagGroups,
    findTags,
    getEventTag,
    getEventTagGroup,
    isEmpty,
} from "./utils";
import api from "../../utils/api";

const baseClass = "ux-iws-search-tag-filter";

@Component({
    tag: "ux-iws-search-tag-filter",
    styleUrl: "search-tag-filter.scss",
})
export class SearchTagFilter {
    @Element() element: HTMLUxIwsSearchTagFilterElement;

    /** Endpoint from which JSON data is fetched */
    @Prop({ reflect: true }) readonly endpoint: string;
    /** Filter data  */
    @Prop({ mutable: true }) data: ISearchFilter = null;
    /** Set this flag if multiple tags can be selected. Only applies for a single level filter. */
    @Prop() multiSelect: boolean = false;
    /** Set this flag if the tags should not collapse on select */
    @Prop() noCollapse: boolean = false;
    /** Set this flag to render a reset filter button  */
    @Prop() resetFilterButton: boolean = false;

    /** The filter selection state */
    @State() selection: SearchFilterSelectionType = [];

    /** Triggered when a tag selection has changed */
    @Event({
        eventName: "uxFilterChange",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    filterChange: EventEmitter;

    private getIsTagActive = (
        tag: ISearchFilter,
        level: number = 0
    ): boolean => {
        const { name, value } = tag;
        if (this.multiSelect) {
            return (
                this.selection.find((param) => param[name] === value) !==
                undefined
            );
        }
        let selectionValue = "";
        if (this.selection[level] !== undefined) {
            selectionValue = this.selection[level][name] || "";
        }
        return selectionValue === tag.value;
    };

    private renderTags = (
        name: string,
        tags: ISearchFilter[],
        level: number = 0
    ) => {
        if (!Array.isArray(tags) || tags.length === 0) {
            return null;
        }

        const isActive = (tag: ISearchFilter): boolean =>
            this.getIsTagActive(tag, level);

        return tags.map((tag) => {
            const tagName = tag.name || name;
            return (
                <ux-iws-search-tag
                    key={`${name}:${tag.value}`}
                    active={isActive(tag)}
                    name={tagName}
                    value={tag.value}
                >
                    {tag.label}
                </ux-iws-search-tag>
            );
        });
    };

    private renderTagGroups = (
        filter: ISearchFilter,
        parentName: string = null,
        level: number = 0
    ) => {
        if (isEmpty(filter)) {
            return null;
        }
        const { tags } = filter || this.data;
        const name = filter.name || parentName;
        const selectionGroupValue =
            this.selection[level] && this.selection[level][name];
        const subGroup =
            this.selection.length < level + 1 ||
            isEmpty(this.selection[level][name])
                ? null
                : tags.find(({ value }) => value === selectionGroupValue);
        const groupTag = isEmpty(selectionGroupValue)
            ? null
            : {
                  ...subGroup,
                  name,
              };
        return (
            <Fragment>
                <ux-iws-search-tag-group
                    name={name}
                    level={level}
                    selectedTag={groupTag}
                >
                    {this.renderTags(name, tags, level)}
                </ux-iws-search-tag-group>
                {subGroup &&
                    Array.isArray(subGroup.tags) &&
                    subGroup.tags.length > 0 &&
                    this.renderTagGroups(subGroup, name, level + 1)}
            </Fragment>
        );
    };

    private updateSelection = (selection: SearchFilterSelectionType) => {
        this.selection = selection;
        window.requestAnimationFrame(() => {
            this.filterChange.emit();
        });
    };

    private onSubmit = (e: Event): void => {
        e.preventDefault();
    };

    private onClickTag = (e: Event): void => {
        const tagElement = getEventTag(e);
        const tagGroup = getEventTagGroup(e);
        if (tagElement === null || tagGroup === null) {
            return;
        }
        const groupLevel = tagGroup.level;
        const groupName = tagGroup.name;
        const groupValue = tagElement.value || "";

        if (tagElement.type === SearchTagTypeEnum.Group) {
            // Clear filter on level
            findTags(tagGroup).forEach((groupTag) => {
                groupTag.active = false;
            });
            this.updateSelection(this.selection.slice(0, groupLevel));
            return;
        }

        if (tagElement.active) {
            if (this.multiSelect) {
                // Uncheck the related tag
                tagElement.active = false;
                this.updateSelection(
                    this.selection.filter(
                        (filter) => filter[tagElement.name] !== tagElement.value
                    )
                );
            } else {
                // Clear filter on level
                findTags(tagGroup).forEach((groupTag) => {
                    groupTag.active = false;
                });
                this.updateSelection(this.selection.slice(0, groupLevel));
            }
            return;
        }

        let selection: SearchFilterSelectionType;

        if (this.multiSelect) {
            tagGroup.expanded = true;
            tagElement.active = true;
            selection = this.selection.slice();
            selection.push({
                [tagElement.name]: tagElement.value,
            });
            this.updateSelection(selection);
            return;
        }

        findTags(tagGroup).forEach((groupTag) => {
            groupTag.active = groupTag === tagElement;
        });
        findTagGroups(this.element).forEach((group) => {
            group.expanded = this.noCollapse || group === tagGroup;
        });
        tagGroup.expanded = this.noCollapse || isEmpty(groupValue);
        tagGroup.selectedTag = {
            label: tagElement.textContent,
            name: tagElement.name,
            value: tagElement.value,
        };
        selection = this.selection.slice(0, groupLevel + 1);
        selection[groupLevel] = {
            [groupName]: groupValue,
        };
        this.updateSelection(selection);
    };

    private onClickReset = (e: Event): void => {
        e.preventDefault();
        findTags(this.element).forEach((tag) => {
            tag.active = isEmpty(tag.value);
        });
        if (this.selection.length > 0) {
            this.updateSelection([]);
        }
    };

    componentWillLoad() {
        if (!isEmpty(this.endpoint)) {
            api<ISearchFilter>(this.endpoint).then((data) => {
                this.data = data;
            });
        }
    }

    render() {
        const { t } = i18nProvider;
        const {
            data,
            onClickTag,
            onClickReset,
            onSubmit,
            resetFilterButton,
            selection,
        } = this;
        const hasActiveFilter =
            selection[0] && !isEmpty(Object.values(selection[0]).join(""));
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`ux-filter`]: true,
                }}
            >
                <form onSubmit={onSubmit}>
                    <div class={`${baseClass}__container`} onClick={onClickTag}>
                        {this.renderTagGroups(data)}
                    </div>
                    {hasActiveFilter && resetFilterButton && (
                        <button
                            class={`${baseClass}__reset`}
                            onClick={onClickReset}
                            type="button"
                        >
                            {t("filter.clearAll")}
                            <ux-icon name="close" />
                        </button>
                    )}
                    <div class={`${baseClass}__slot`}>
                        <slot />
                    </div>
                </form>
            </Host>
        );
    }
}
